import { CopyOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Button, Form, Input, message, Modal, Popover, Select, Tag, Typography } from 'antd'
import { StakingTokenContractType } from 'Modules'
import { useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import LinkAddress from 'component/link/link-address'
import {
  CompoundTypeLabel,
  StakingPoolTypeEnum,
  StakingPoolTypeLabel,
  StakingTokenTypeLabel,
} from 'constants/staking'
import { convertPercentContract, convertUnitsContract } from 'utils/contract'
import { formatTimeStamp } from 'utils/format-time'
import { isValidETHAddress } from 'utils/validate-fields'

const { Text } = Typography

interface IModalStakingToken {
  form: any
  modalMode: 'create' | 'update' | string
  isModalOpen: boolean
  setIsModalOpen: (isModalOpen: boolean) => void
  onSubmit: () => void
}

const ModalCreateOrUpdate = ({
  form,
  modalMode,
  isModalOpen,
  setIsModalOpen,
  onSubmit,
}: IModalStakingToken) => {
  const [dataViewInfo, setDataViewInfo] = useState<StakingTokenContractType | null>(null)

  const statusOptions = [
    {
      value: false,
      label: 'Disable',
    },
    {
      value: true,
      label: 'Enable',
    },
  ]

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 14 },
    },
  }

  const handleOk = () => {
    form
      .validateFields()
      .then(async (values: any) => {
        const { poolType, compoundType, ...rest } = values
        const payload = {
          ...rest,
          poolClassify: poolType,
          compoundType: poolType === StakingPoolTypeEnum.COMPOUND ? compoundType : null,
        }

        if (modalMode === 'update') {
          const { _id, ...dataEdit } = payload
          const response = await AppAdmin.updateStakingToken(_id, dataEdit)
          setIsModalOpen(false)
          message.success(response.data.message)
          onSubmit?.()
        } else {
          const response = await AppAdmin.createStakingToken(payload)
          setIsModalOpen(false)
          message.success(response.data.message)
          onSubmit?.()
        }
      })
      .catch((error: any) => {
        message.error(error.message || error.errorFields?.[0]?.errors?.[0])

        // Error message format = "[errorField] [reason]"
        if (error && error.message) {
          form.setFields([
            {
              name: error.message.split(' ')[0],
              errors: [error.message],
            },
          ])
        }
      })
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setDataViewInfo(null)
    form.resetFields()
  }

  const viewInfo = async (contractAddress: string) => {
    if (dataViewInfo) {
      setDataViewInfo(null)
      return
    }

    if (form.getFieldError('contractAddress').length) {
      return
    }

    const poolType = form.getFieldValue('poolType')
    if (poolType === undefined || poolType === null) {
      message.warning('Please select Pool Type first')
      return
    }

    try {
      const response = await AppAdmin.getStakingTokenContractInfo(contractAddress, poolType)
      const dataView = response.data.data

      if (response.data.success === true) {
        setDataViewInfo(dataView)
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to get contract info')

      // Error message format = "[errorField] [reason]"
      if (error && error.message) {
        form.setFields([
          {
            name: error.message.split(' ')[0],
            errors: [error.message],
          },
        ])
      }
    }
  }

  return (
    <Modal
      title={modalMode === 'update' ? 'Update Staking Pool' : 'Create Staking Pool'}
      open={isModalOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      className="modalStaking"
      destroyOnClose={true}
      width={750}
      centered
      okText={modalMode === 'update' ? 'Save' : 'Create'}
    >
      <Form form={form} {...formItemLayout} style={{ maxWidth: 750 }}>
        {modalMode === 'update' ? (
          <Form.Item
            label={
              <span>
                Pool ID
                <Popover
                  content={
                    <>
                      <p>The ID of the pool is used to identify the pool in the database.</p>
                      <p>Pool Id cannot be changed</p>
                    </>
                  }
                  placement="top"
                  title="Explain"
                >
                  <span style={{ marginLeft: 5 }}>
                    <QuestionCircleOutlined />
                  </span>
                </Popover>
              </span>
            }
            name="_id"
          >
            <Input disabled />
          </Form.Item>
        ) : null}

        <Form.Item
          label={
            <span>
              Pool Type
              <Popover
                content={
                  <>
                    <p>The name of the pool is displayed on the web.</p>
                    <p>
                      Users are not allowed to enter special characters and must not exceed 30
                      characters
                    </p>
                  </>
                }
                placement="top"
                title="Explain"
              >
                <span style={{ marginLeft: 5 }}>
                  <QuestionCircleOutlined />
                </span>
              </Popover>
            </span>
          }
          name="name"
          rules={[
            { required: true, message: 'Please input Pool Type!' },
            {
              validator(_, value) {
                if (!value) {
                  return Promise.reject()
                }

                if (value.length > 30) {
                  return Promise.reject(new Error('Input must not exceed 30 characters.'))
                }

                if (/^[a-zA-Z0-9\s-]+$/.test(value)) {
                  return Promise.resolve()
                } else {
                  return Promise.reject(new Error('Pool Type cannot contain special characters!'))
                }
              },
            },
          ]}
        >
          <Input placeholder="Enter Pool Type" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label={
            <span>
              Pool Type
              <Popover
                content={
                  <div>
                    <p>The type of the pool. Fixed or Compound.</p>
                  </div>
                }
                placement="top"
                title="Explain"
              >
                <span style={{ marginLeft: 5 }}>
                  <QuestionCircleOutlined />
                </span>
              </Popover>
            </span>
          }
          name="poolType"
          rules={[{ required: true, message: 'Please select Pool Type!' }]}
        >
          <Select placeholder="Choose Pool Type" options={StakingPoolTypeLabel} />
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.poolType !== cur.poolType}>
          {({ getFieldValue }) =>
            getFieldValue('poolType') === StakingPoolTypeEnum.COMPOUND ? (
              <Form.Item
                label={
                  <span>
                    Compound Type
                    <Popover
                      content={
                        <div>
                          <p>The compound frequency of the pool.</p>
                        </div>
                      }
                      placement="top"
                      title="Explain"
                    >
                      <span style={{ marginLeft: 5 }}>
                        <QuestionCircleOutlined />
                      </span>
                    </Popover>
                  </span>
                }
                name="compoundType"
                rules={[{ required: true, message: 'Please select Compound Type!' }]}
              >
                <Select placeholder="Choose Compound Type" options={CompoundTypeLabel} />
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item
          label={
            <span>
              Pool Contract Address
              <Popover
                content={
                  <p>The contract address of the pool. This is used to get the pool information.</p>
                }
                placement="top"
                title="Explain"
              >
                <span style={{ marginLeft: 5 }}>
                  <QuestionCircleOutlined />
                </span>
              </Popover>
            </span>
          }
          style={{ marginBottom: 0 }}
        >
          <div className="pool-contract">
            <Form.Item
              name="contractAddress"
              rules={[
                {
                  required: true,
                  message: 'Please input Pool Contract!',
                },
                {
                  validator(_, value) {
                    if (!value) {
                      return Promise.resolve()
                    }
                    if (isValidETHAddress(value) === false) {
                      return Promise.reject('Invalid Pool Contract')
                    }
                    return Promise.resolve()
                  },
                },
              ]}
              style={{ width: '78%' }}
            >
              <Input placeholder="Enter Pool Contract" />
            </Form.Item>

            <Button
              type="primary"
              className="btn-edit"
              style={{ width: '20%', fontSize: 12, height: 30 }}
              onClick={() => viewInfo(form.getFieldValue('contractAddress'))}
            >
              {dataViewInfo ? 'Hide Info' : 'Show Info'}
            </Button>
          </div>

          {dataViewInfo && (
            <div
              style={{
                marginBottom: 20,
                marginTop: -8,
                display: 'flex',
                flexDirection: 'column',
                gap: 5,
              }}
            >
              <div>
                <span>Token Stake Address: </span> &nbsp; &nbsp;
                <Text ellipsis style={{ height: 28 }}>
                  <LinkAddress
                    address={dataViewInfo.tokenInfo?.contractAddress}
                    icon={<CopyOutlined />}
                    max={5}
                    render={(address) => {
                      return <span>{address}</span>
                    }}
                  />
                </Text>
              </div>

              <div>
                <span>Symbol: </span> &nbsp; &nbsp;
                {dataViewInfo.tokenInfo?.tokenSymbol}
              </div>

              <div>
                <span>APR: </span> &nbsp; &nbsp;
                <Tag color="green">{`${convertPercentContract(dataViewInfo.stakingAPR)}%`}</Tag>
              </div>

              {/* <div>
                <span>Pool Cap: </span> &nbsp; &nbsp;
                {dataViewInfo.}
              </div> */}

              <div>
                <span>Early Unstake Fee: </span> &nbsp; &nbsp;
                <Tag color="green">
                  {`${convertPercentContract(dataViewInfo.earlyUnstakeFee)}%`}
                </Tag>
              </div>

              <div>
                <span>Start Time: </span> &nbsp; &nbsp;
                {dataViewInfo.startTime === 0 ? 'Started' : formatTimeStamp(dataViewInfo.startTime)}
              </div>

              <div>
                <span>End Time: </span> &nbsp; &nbsp;
                {dataViewInfo.endTime === 0 ? 'No end time' : formatTimeStamp(dataViewInfo.endTime)}
              </div>

              {dataViewInfo.minStakeAmount != null && (
                <div>
                  <span>Min Stake Amount: </span> &nbsp; &nbsp;
                  {convertUnitsContract(
                    dataViewInfo.minStakeAmount,
                    dataViewInfo.tokenInfo?.tokenDecimal
                  )}
                </div>
              )}

              {dataViewInfo.compoundInterval != null && (
                <div>
                  <span>Compound Interval: </span> &nbsp; &nbsp;
                  {dataViewInfo.compoundInterval}s
                </div>
              )}
            </div>
          )}
        </Form.Item>

        <Form.Item
          label={
            <span>
              Pool Period
              <Popover
                content={
                  <div>
                    <p>The duration of the pool. This is used to get the pool information.</p>
                  </div>
                }
                placement="top"
                title="Explain"
              >
                <span style={{ marginLeft: 5 }}>
                  <QuestionCircleOutlined />
                </span>
              </Popover>
            </span>
          }
          name="type"
          rules={[{ required: true, message: 'Please input type!' }]}
        >
          <Select placeholder="Choose type" options={StakingTokenTypeLabel} />
        </Form.Item>

        <Form.Item
          label={
            <span>
              Pool Status
              <Popover
                content={
                  <div>
                    <p>Enable: The Pool is active and shows on the web.</p>
                    <p>Disable: The Pool is not active and does not show on web.</p>
                  </div>
                }
                placement="top"
                title="Explain"
              >
                <span style={{ marginLeft: 5 }}>
                  <QuestionCircleOutlined />
                </span>
              </Popover>
            </span>
          }
          name="isActive"
          rules={[{ required: true, message: 'Please input Status!' }]}
        >
          <Select placeholder="Choose Status" options={statusOptions} />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default ModalCreateOrUpdate
